import React, { useState, useEffect, useRef } from 'react';
import { Stage, Layer, Rect, Circle, Line, Text, Group } from 'react-konva';
import { useFamilyTree } from '../contexts/FamilyTreeContext';
import { Individual, FamilyUnit, Gender } from '../models/types';
import { useTranslation } from 'react-i18next';

interface FamilyTreeCanvasProps {
  width: number;
  height: number;
}

interface DetailPopupProps {
  individual: Individual | null;
  position: { x: number, y: number } | null;
  onClose: () => void;
}

// Format date function to remove time component and display YYYY-MM-DD
export const formatDate = (dateString: string | null): string => {
  if (!dateString) return '';
  
  // More robust date handling to strip away time component
  try {
    // First try parsing as a date
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date.toISOString().split('T')[0];
    }
    
    // Fallback: Just take first 10 characters if it's in ISO format
    if (dateString.length >= 10 && dateString.includes('-')) {
      return dateString.substring(0, 10);
    }
    
    // Last resort: return as is
    return dateString;
  } catch (e) {
    // If all else fails, return original string
    return dateString;
  }
};

const DetailPopup: React.FC<DetailPopupProps> = ({ individual, position, onClose }) => {
  if (!individual || !position) return null;
  
  const hasProfilePhoto = individual.photos && individual.photos.length > 0;
  
  return (
    <div
      className="detail-popup"
      style={{
        position: 'absolute',
        left: position.x + 20,
        top: position.y,
        backgroundColor: 'white',
        border: '1px solid #ccc',
        borderRadius: '5px',
        padding: '10px',
        boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
        zIndex: 1000,
        maxWidth: '300px'
      }}
    >
      <button 
        onClick={onClose}
        style={{
          position: 'absolute',
          right: '5px',
          top: '5px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontWeight: 'bold'
        }}
      >
        ×
      </button>
      
      <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '10px' }}>
        {hasProfilePhoto && (
          <div style={{ 
            marginRight: '10px', 
            borderRadius: '50%', 
            overflow: 'hidden',
            width: '60px',
            height: '60px',
            flexShrink: 0,
          }}>
            <img 
              src={individual.photos?.[0]} 
              alt={`${individual.firstName} ${individual.lastName}`} 
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </div>
        )}
        
        <div>
          <h3 style={{ margin: '0 0 5px 0' }}>
            {individual.firstName} {individual.middleName ? individual.middleName + ' ' : ''}{individual.lastName}
          </h3>
          <div>Gender: {individual.gender}</div>
        </div>
      </div>
      
      {individual.birthDate && <div>Birth: {formatDate(individual.birthDate)}</div>}
      {individual.deathDate && <div>Death: {formatDate(individual.deathDate)}</div>}
      {individual.birthPlace && <div>Birthplace: {individual.birthPlace}</div>}
      {individual.occupation && <div>Occupation: {individual.occupation}</div>}
      {individual.education && <div>Education: {individual.education}</div>}
      {individual.notes && <div>Notes: {individual.notes}</div>}
    </div>
  );
};

const FamilyTreeCanvas: React.FC<FamilyTreeCanvasProps> = ({ width, height }) => {
  const { familyTree, loading } = useFamilyTree();
  const { t } = useTranslation();
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: width / 5, y: height / 4 });
  const [dragging, setDragging] = useState(false);
  const [selectedIndividual, setSelectedIndividual] = useState<Individual | null>(null);
  const [popupPosition, setPopupPosition] = useState<{ x: number, y: number } | null>(null);
  const [hoveredIndividual, setHoveredIndividual] = useState<string | null>(null);
  const [hoveredConnectionPoint, setHoveredConnectionPoint] = useState<string | null>(null);
  const stageRef = useRef<any>(null);
  
  // Create a mapping for sequential IDs
  const [sequentialIds, setSequentialIds] = useState<Record<string, number>>({});
  
  // Initialize sequential IDs
  useEffect(() => {
    if (!loading && familyTree) {
      const idMapping: Record<string, number> = {};
      familyTree.individuals.forEach((individual, index) => {
        idMapping[individual.id] = index + 1;
      });
      setSequentialIds(idMapping);
    }
  }, [familyTree, loading]);
  
  // For dragging individuals and connection points
  const [isDraggingIndividual, setIsDraggingIndividual] = useState(false);
  const [individualPositions, setIndividualPositions] = useState<Record<string, { x: number, y: number }>>({});
  const [familyUnitPositions, setFamilyUnitPositions] = useState<Record<string, { x: number, y: number }>>({});
  
  // For keeping track of child connection points
  const [childConnectionPoints, setChildConnectionPoints] = useState<
    Record<string, { familyUnitId: string, x: number, y: number }>
  >({});
  
  // Calculate positions for individuals and family units
  useEffect(() => {
    if (!loading && familyTree) {
      const calculatePositions = () => {
        const positions: Record<string, { x: number, y: number }> = {};
        const unitPositions: Record<string, { x: number, y: number }> = {};
        
        // Start with the oldest generation (those without parents)
        const individualsWithParents = new Set(
          familyTree.childRelationships.map(rel => rel.childId)
        );
        
        const rootIndividuals = familyTree.individuals.filter(
          ind => !individualsWithParents.has(ind.id)
        );
        
        // Center root individuals horizontally
        const totalWidth = rootIndividuals.length * 200;
        const startX = Math.max(300, (width / 2) - (totalWidth / 2));
        
        // Position root individuals at the top
        rootIndividuals.forEach((ind, index) => {
          positions[ind.id] = {
            x: startX + index * 200,
            y: 100
          };
        });
        
        // Position family units
        familyTree.familyUnits.forEach((unit, index) => {
          // Find the average x position of partners
          const partner1Pos = positions[unit.partners[0]];
          const partner2Pos = positions[unit.partners[1]];
          
          if (partner1Pos && partner2Pos) {
            const x = (partner1Pos.x + partner2Pos.x) / 2;
            const y = partner1Pos.y + 100;
            
            unitPositions[unit.id] = { x, y };
            
            // Position children
            if (unit.children && unit.children.length > 0) {
              const childrenWidth = unit.children.length * 150;
              const startChildX = x - childrenWidth / 2 + 75;
              
              unit.children.forEach((childId, childIndex) => {
                positions[childId] = {
                  x: startChildX + childIndex * 150,
                  y: y + 100
                };
              });
            }
          } else {
            // If partners don't have positions yet, place them somewhere
            const x = startX + index * 200;
            const y = 300;
            
            unitPositions[unit.id] = { x, y };
            
            // Position partners if they don't have positions
            if (!partner1Pos) {
              positions[unit.partners[0]] = { x: x - 75, y: y - 100 };
            }
            
            if (!partner2Pos) {
              positions[unit.partners[1]] = { x: x + 75, y: y - 100 };
            }
            
            // Position children
            if (unit.children && unit.children.length > 0) {
              const childrenWidth = unit.children.length * 150;
              const startChildX = x - childrenWidth / 2 + 75;
              
              unit.children.forEach((childId, childIndex) => {
                positions[childId] = {
                  x: startChildX + childIndex * 150,
                  y: y + 100
                };
              });
            }
          }
        });
        
        setIndividualPositions(positions);
        setFamilyUnitPositions(unitPositions);
      };
      
      calculatePositions();
    }
  }, [familyTree, loading, width]);
  
  // Initialize child connection points if they don't exist
  useEffect(() => {
    if (!loading && familyTree) {
      const newChildConnections: Record<string, { familyUnitId: string, x: number, y: number }> = {};
      
      // For each family unit with children
      familyTree.familyUnits.forEach(unit => {
        if (unit.children && unit.children.length > 0) {
          const unitPos = familyUnitPositions[unit.id];
          
          if (unitPos) {
            // Create connection point for each child
            unit.children.forEach(childId => {
              const childPos = individualPositions[childId];
              
              if (childPos) {
                const connectionKey = `${unit.id}-${childId}`;
                
                // Only initialize if it doesn't exist already
                if (!childConnectionPoints[connectionKey]) {
                  newChildConnections[connectionKey] = {
                    familyUnitId: unit.id,
                    x: childPos.x,
                    y: unitPos.y + 50  // The Y coordinate where horizontal child line is drawn
                  };
                } else {
                  // Keep existing positions
                  newChildConnections[connectionKey] = childConnectionPoints[connectionKey];
                }
              }
            });
          }
        }
      });
      
      // Update state only if we have new connections
      if (Object.keys(newChildConnections).length > 0) {
        setChildConnectionPoints(prev => ({
          ...prev,
          ...newChildConnections
        }));
      }
    }
  }, [familyTree, loading, familyUnitPositions, individualPositions, childConnectionPoints]);
  
  if (loading) {
    return <div>Loading family tree data...</div>;
  }
  
  // Handle wheel event for zooming
  const handleWheel = (e: any) => {
    e.evt.preventDefault();
    
    const scaleBy = 1.1;
    const stage = stageRef.current;
    const pointer = stage.getPointerPosition();
    
    const mousePointTo = {
      x: (pointer.x - stage.x()) / stage.scaleX(),
      y: (pointer.y - stage.y()) / stage.scaleY(),
    };
    
    const newScale = e.evt.deltaY < 0 ? scale * scaleBy : scale / scaleBy;
    
    setScale(newScale);
    setPosition({
      x: pointer.x - mousePointTo.x * newScale,
      y: pointer.y - mousePointTo.y * newScale,
    });
  };
  
  // Handle dragging the canvas
  const handleDragStart = () => {
    if (!isDraggingIndividual) {
      setDragging(true);
    }
  };
  
  const handleDragEnd = () => {
    setDragging(false);
  };
  
  const handleDragMove = (e: any) => {
    if (!dragging) return;
    
    const stage = e.target.getStage();
    setPosition({
      x: stage.x(),
      y: stage.y(),
    });
  };
  
  // Handle individual node selection
  const handleNodeClick = (individual: Individual, e: any) => {
    const stage = stageRef.current;
    const position = stage.getPointerPosition();
    
    setSelectedIndividual(individual);
    setPopupPosition({
      x: position.x,
      y: position.y
    });
  };
  
  // Close detail popup
  const handleClosePopup = () => {
    setSelectedIndividual(null);
    setPopupPosition(null);
  };
  
  // Drag individual node
  const handleIndividualDragStart = (id: string) => {
    setIsDraggingIndividual(true);
  };
  
  const handleIndividualDragEnd = (id: string, e: any) => {
    setIsDraggingIndividual(false);
    
    // Update position
    const newPositions = {...individualPositions};
    newPositions[id] = {
      x: e.target.x(),
      y: e.target.y()
    };
    
    setIndividualPositions(newPositions);
  };
  
  // Handle connection point dragging
  const handleConnectionPointDragStart = (id: string) => {
    // No need to track this state since we're not using it
  };
  
  const handleConnectionPointDragEnd = (id: string, type: 'family-unit' | 'child-connection', e: any) => {
    if (type === 'family-unit') {
      // Update family unit connection point position
      const newPositions = {...familyUnitPositions};
      newPositions[id] = {
        x: e.target.x(),
        y: e.target.y()
      };
      
      setFamilyUnitPositions(newPositions);
    } else if (type === 'child-connection') {
      // Update child connection point
      const newPositions = {...childConnectionPoints};
      if (newPositions[id]) {
        newPositions[id] = {
          ...newPositions[id],
          x: e.target.x(),
          y: e.target.y()
        };
        
        setChildConnectionPoints(newPositions);
      }
    }
  };
  
  // Render individual node
  const renderIndividual = (individual: Individual) => {
    const position = individualPositions[individual.id];
    
    if (!position) return null;
    
    const isHovered = hoveredIndividual === individual.id;
    const isSelected = selectedIndividual?.id === individual.id;
    const nodeWidth = 100;
    const nodeHeight = 120;
    const padding = 10;
    
    const genderSymbol = individual.gender === Gender.MALE ? '♂' : '♀';
    const dateText = individual.birthDate ? 
      (individual.deathDate ? 
        `${formatDate(individual.birthDate)} - ${formatDate(individual.deathDate)}` : 
        `${formatDate(individual.birthDate)}`) : 
      '';
    
    // Use sequential ID instead of parsed ID
    const displayId = sequentialIds[individual.id] || 1;
    
    return (
      <Group 
        key={individual.id} 
        x={position.x} 
        y={position.y}
        draggable
        onDragStart={() => handleIndividualDragStart(individual.id)}
        onDragEnd={(e) => handleIndividualDragEnd(individual.id, e)}
        onClick={(e) => handleNodeClick(individual, e)}
        onMouseEnter={() => setHoveredIndividual(individual.id)}
        onMouseLeave={() => setHoveredIndividual(null)}
      >
        {individual.gender === Gender.MALE ? (
          <Rect
            width={nodeWidth}
            height={nodeHeight}
            fill={isHovered ? "skyblue" : "lightblue"}
            stroke={isSelected ? "blue" : "black"}
            strokeWidth={isSelected ? 2 : 1}
            cornerRadius={3}
            x={-nodeWidth / 2}
            y={-nodeHeight / 2}
            shadowColor={isHovered ? "gray" : undefined}
            shadowBlur={isHovered ? 5 : 0}
            shadowOffset={isHovered ? { x: 2, y: 2 } : undefined}
            shadowOpacity={0.5}
          />
        ) : (
          <Rect
            width={nodeWidth}
            height={nodeHeight}
            fill={isHovered ? "lightpink" : "pink"}
            stroke={isSelected ? "red" : "black"}
            strokeWidth={isSelected ? 2 : 1}
            cornerRadius={3}
            x={-nodeWidth / 2}
            y={-nodeHeight / 2}
            shadowColor={isHovered ? "gray" : undefined}
            shadowBlur={isHovered ? 5 : 0}
            shadowOffset={isHovered ? { x: 2, y: 2 } : undefined}
            shadowOpacity={0.5}
          />
        )}
        
        {/* Birth/Death Date */}
        <Text
          text={dateText}
          fontSize={11}
          align="center"
          width={nodeWidth - padding * 2}
          x={-nodeWidth / 2 + padding}
          y={-nodeHeight / 2 + padding + 5}
          fill={isHovered ? "#333" : "black"}
        />
        
        {/* Full Name */}
        <Text
          text={`${individual.firstName} ${individual.middleName ? individual.middleName + ' ' : ''}${individual.lastName}`}
          fontSize={12}
          align="center"
          width={nodeWidth - padding * 2}
          x={-nodeWidth / 2 + padding}
          y={-nodeHeight / 2 + nodeHeight/3}
          fill={isHovered ? "#333" : "black"}
          fontStyle="bold"
        />
        
        {/* Gender Symbol */}
        <Text
          text={genderSymbol}
          fontSize={18}
          align="center"
          width={20}
          x={nodeWidth / 2 - 25}
          y={nodeHeight / 2 - 35}
          fill={isHovered ? "#333" : "black"}
        />
        
        {/* ID Circle */}
        <Circle
          radius={12}
          fill="white"
          stroke="black"
          strokeWidth={1}
          x={-nodeWidth / 2 + 25}
          y={nodeHeight / 2 - 25}
        />
        <Text
          text={displayId.toString()}
          fontSize={12}
          align="center"
          width={20}
          x={-nodeWidth / 2 + 15}
          y={nodeHeight / 2 - 30}
          fill="black"
        />
      </Group>
    );
  };
  
  // Render family unit with draggable connection points
  const renderFamilyUnit = (familyUnit: FamilyUnit) => {
    const position = familyUnitPositions[familyUnit.id];
    
    if (!position) return null;
    
    const { x, y } = position;
    const partner1Pos = individualPositions[familyUnit.partners[0]];
    const partner2Pos = individualPositions[familyUnit.partners[1]];
    
    if (!partner1Pos || !partner2Pos) return null;
    
    const isConnectionHovered = hoveredConnectionPoint === familyUnit.id;
    
    // Lines connecting partners to family unit
    const lines = [
      <Line
        key={`line-p1-${familyUnit.id}`}
        points={[partner1Pos.x, partner1Pos.y, x, y]}
        stroke="black"
        strokeWidth={1}
      />,
      <Line
        key={`line-p2-${familyUnit.id}`}
        points={[partner2Pos.x, partner2Pos.y, x, y]}
        stroke="black"
        strokeWidth={1}
      />
    ];
    
    // Lines connecting family unit to children
    if (familyUnit.children && familyUnit.children.length > 0) {
      // Vertical line down from family unit connection point
      lines.push(
        <Line
          key={`line-down-${familyUnit.id}`}
          points={[x, y, x, y + 50]}
          stroke="black"
          strokeWidth={1}
        />
      );
      
      // For each child, check if there's a custom connection point
      familyUnit.children.forEach(childId => {
        const childPos = individualPositions[childId];
        const connectionKey = `${familyUnit.id}-${childId}`;
        const connectionPoint = childConnectionPoints[connectionKey];
        
        if (childPos) {
          if (connectionPoint) {
            // If a custom connection point exists, use it
            lines.push(
              <Line
                key={`line-child-custom-${childId}`}
                points={[x, y + 50, connectionPoint.x, connectionPoint.y, childPos.x, childPos.y]}
                stroke="black"
                strokeWidth={1}
              />
            );
            
            // Render a draggable connection point
            const isChildConnectionHovered = hoveredConnectionPoint === connectionKey;
            lines.push(
              <Group
                key={`connection-point-${connectionKey}`}
                x={connectionPoint.x}
                y={connectionPoint.y}
                draggable
                onDragStart={() => handleConnectionPointDragStart(connectionKey)}
                onDragEnd={(e) => handleConnectionPointDragEnd(connectionKey, 'child-connection', e)}
                onMouseEnter={() => setHoveredConnectionPoint(connectionKey)}
                onMouseLeave={() => setHoveredConnectionPoint(null)}
              >
                <Circle
                  radius={6}
                  fill={isChildConnectionHovered ? "orange" : "white"}
                  stroke="black"
                  strokeWidth={1}
                />
              </Group>
            );
          } else {
            // Fallback to direct line if no custom connection point
            lines.push(
              <Line
                key={`line-child-${childId}`}
                points={[childPos.x, y + 50, childPos.x, childPos.y]}
                stroke="black"
                strokeWidth={1}
              />
            );
          }
        }
      });
      
      // Create horizontal line connecting all child connection points
      const connectionPoints = familyUnit.children
        .map(childId => {
          const connectionKey = `${familyUnit.id}-${childId}`;
          return childConnectionPoints[connectionKey];
        })
        .filter(Boolean);
      
      if (connectionPoints.length > 1) {
        // Find leftmost and rightmost connection points
        const leftmostX = Math.min(...connectionPoints.map(point => point.x));
        const rightmostX = Math.max(...connectionPoints.map(point => point.x));
        
        lines.push(
          <Line
            key={`line-children-${familyUnit.id}`}
            points={[leftmostX, y + 50, rightmostX, y + 50]}
            stroke="black"
            strokeWidth={1}
          />
        );
      }
    }
    
    return (
      <Group key={familyUnit.id}>
        {lines}
        <Group
          x={x}
          y={y}
          draggable
          onDragStart={() => handleConnectionPointDragStart(familyUnit.id)}
          onDragEnd={(e) => handleConnectionPointDragEnd(familyUnit.id, 'family-unit', e)}
          onMouseEnter={() => setHoveredConnectionPoint(familyUnit.id)}
          onMouseLeave={() => setHoveredConnectionPoint(null)}
        >
          <Rect
            x={-10}
            y={-10}
            width={20}
            height={20}
            fill={isConnectionHovered ? "orange" : "white"}
            stroke="black"
            strokeWidth={1}
            cornerRadius={3}
          />
        </Group>
      </Group>
    );
  };
  
  const containerStyle: React.CSSProperties = {
    position: 'relative',
    width: '100%',
    height: '100vh',
    overflow: 'hidden',
    backgroundColor: '#fdfcff', // MD3 background color
  };

  const headerStyle: React.CSSProperties = {
    backgroundColor: '#006495', // MD3 primary color
    color: '#ffffff', // MD3 on-primary color
    padding: '0.5rem 1rem',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  };

  const buttonStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '2.5rem',
    padding: '0 1.5rem',
    border: 'none',
    borderRadius: '9999px',
    backgroundColor: '#006495', // MD3 primary color
    color: '#ffffff', // MD3 on-primary color
    fontFamily: 'Roboto, sans-serif',
    fontSize: '0.875rem',
    fontWeight: 500,
    letterSpacing: '0.1px',
    cursor: 'pointer',
    transition: 'all 250ms ease',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.3), 0 1px 3px 1px rgba(0, 0, 0, 0.15)',
    margin: '0.5rem',
  };

  const buttonHoverStyle: React.CSSProperties = {
    backgroundColor: '#004b70', // Slightly darker primary color for hover
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.3), 0 2px 6px 2px rgba(0, 0, 0, 0.15)',
  };

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <h2 style={{ margin: 0, fontWeight: 500 }}>{t('Family Tree')}</h2>
        <div>
          <button 
            style={buttonStyle} 
            onClick={() => setScale(scale + 0.1)}
            onMouseOver={(e) => Object.assign(e.currentTarget.style, buttonHoverStyle)}
            onMouseOut={(e) => Object.assign(e.currentTarget.style, buttonStyle)}
          >
            {t('Zoom In')}
          </button>
          <button 
            style={buttonStyle}
            onClick={() => setScale(Math.max(0.1, scale - 0.1))}
            onMouseOver={(e) => Object.assign(e.currentTarget.style, buttonHoverStyle)}
            onMouseOut={(e) => Object.assign(e.currentTarget.style, buttonStyle)}
          >
            {t('Zoom Out')}
          </button>
          <button 
            style={buttonStyle}
            onClick={() => {
              setScale(1);
              setPosition({ x: width / 2, y: height / 4 });
            }}
            onMouseOver={(e) => Object.assign(e.currentTarget.style, buttonHoverStyle)}
            onMouseOut={(e) => Object.assign(e.currentTarget.style, buttonStyle)}
          >
            {t('Reset View')}
          </button>
        </div>
      </div>

      <Stage
        width={window.innerWidth}
        height={window.innerHeight - 60}
        ref={stageRef}
        draggable
        onDragStart={() => setDragging(true)}
        onDragEnd={() => setDragging(false)}
        onWheel={handleWheel}
        scale={{ x: scale, y: scale }}
        x={position.x}
        y={position.y}
      >
        <Layer>
          {familyTree.familyUnits.map(renderFamilyUnit)}
          {familyTree.individuals.map(renderIndividual)}
        </Layer>
      </Stage>

      {selectedIndividual && popupPosition && (
        <DetailPopup
          individual={selectedIndividual}
          position={popupPosition}
          onClose={() => {
            setSelectedIndividual(null);
            setPopupPosition(null);
          }}
        />
      )}
    </div>
  );
};

export default FamilyTreeCanvas;
